import { authClient } from "@/api/auth";
import { client } from "@/hono";
import { useNavigation } from "@/hooks";
import { useAppShellStore } from "@msuite/ui/app-shell";
import { ColorModeIcon, useColorModeContext } from "@msuite/ui/color-mode";
import { CountryFlagIcon } from "@msuite/ui/country-flag-icon";
import { useDisclosure } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { i18n, useTranslation } from "@msuite/ui/localization";
import type { MenuItems } from "@msuite/ui/menu";
import { useQueryClient } from "@msuite/ui/query";
import type { Locale } from "@msuite/utils/locales";
import { locales } from "@msuite/utils/locales";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { useChatStore } from "../components/chat/store";
import { useAuth } from "./use-auth";
import { useNavigationOrganizations } from "./use-navigation-organizations";
import { formatNumber } from "@msuite/utils/numbers";

export function useNavigationSetup() {
	/** Store */
	const chatStore = useChatStore();
	const appShellStore = useAppShellStore();

	/** Hooks */
	const navigate = useNavigate();
	const navigationItems = useNavigation();
	const { toggleColorMode, colorMode } = useColorModeContext();
	const { t } = useTranslation();
	const { isSignedIn, user } = useAuth();
	const { createOrganizationNavigationItems } = useNavigationOrganizations();
	const suggestTranslationModal = useDisclosure();
	const organizationSettingsModal = useDisclosure();
	const profileModal = useDisclosure();
	const qc = useQueryClient();

	/** Functions */
	async function handleOnChangeLanguage(language: Locale) {
		i18n.changeLanguage(language);
		localStorage.setItem("language", language);
		if (!user?.id) return;
		await client.users.language.$post({
			json: { language },
		});
		window.location.reload();
	}

	async function handleOnSignOut() {
		await authClient.signOut();
		localStorage.clear();
		qc.clear();
	}

	function handleOnNavigateToOrganizationSettings() {
		navigate("/organization/settings");
	}

	const menuItems = useMemo(() => {
		if (!user) return [] as MenuItems;
		return [
			{
				icon: icon("User"),
				label: `${user.firstName} ${user.lastName}`,
			},
			{
				icon: icon("Settings"),
				label: t("settings.settings"),
				onClick: profileModal.onOpen,
			},
			"divider",
			{
				icon: icon("MessageSquare"),
				label: t("chats.chats"),
				onClick: chatStore.handleOnOpen,
			},
			{
				icon: icon("Layout"),
				label: t("navigation.layout"),
				onClick: undefined,
				items: [
					{
						icon: icon("AppWindow"),
						label: t("navigation.topbar_layout"),
						onClick: () => appShellStore.setLayout("topbar"),
						isActive: appShellStore.layout === "topbar",
					},
					{
						icon: icon("Sidebar"),
						label: t("navigation.sidebar_layout"),
						onClick: () => appShellStore.setLayout("sidebar"),
						isActive: appShellStore.layout === "sidebar",
					},
				],
			},
			{
				icon: icon("ALargeSmall"),
				label: t("misc.scale"),
				items: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((n) => ({
					label: `${formatNumber(n, "decimal")}x`,
					icon: icon("ALargeSmall"),
					onClick: () => appShellStore.setFontScale(n),
					isActive: appShellStore.fontScale === n,
				})),
			},
			"divider",
			t("organization.organizations"),
			...createOrganizationNavigationItems(),
			{
				icon: icon("Settings"),
				label: t("organization.settings"),
				onClick: handleOnNavigateToOrganizationSettings,
				cyId: "organization-settings",
			},
			"divider",
			{
				icon: ColorModeIcon,
				keepOpen: true,
				label: `${t("theme.theme")}: ${colorMode === "dark" ? t("theme.dark") : t("theme.light")}`,
				onClick: toggleColorMode,
			},
			{
				icon: icon("Languages"),
				items: [
					...locales.map((locale) => ({
						icon: () => <CountryFlagIcon locale={locale} />,
						label: t(`localization.${locale}`),
						isActive: i18n.language === locale,
						onClick: () => {
							handleOnChangeLanguage(locale);
						},
					})),
					"divider",
					{
						icon: icon("BookPlus"),
						label: t("localization.suggest_better_translation"),
						onClick: suggestTranslationModal.onOpen,
					},
				],
				label: t("localization.language"),
				onClick: undefined,
			},
			"divider",
			{
				icon: icon("Headset"),
				isDanger: false,
				label: t("misc.command_view"),
				onClick: () => {
					navigate("/command");
				},
				cyId: "command-view",
			},
			"divider",
			{
				icon: icon("LogOut"),
				isDanger: true,
				label: t("auth.signout"),
				onClick: handleOnSignOut,
				cyId: "sign-out-button",
			},
		] as MenuItems;
	}, [
		user,
		t,
		colorMode,
		toggleColorMode,
		chatStore,
		appShellStore,
		createOrganizationNavigationItems,
		suggestTranslationModal,
		organizationSettingsModal,
		profileModal,
	]);

	return {
		menuItems,
		navigationItems,
		isSignedIn,
		organizationSettingsModal,
		suggestTranslationModal,
		profileModal,
	};
}
